import {
  type AppError,
  NotFoundError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {type UseQueryOptions, useQuery} from '@tanstack/react-query';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  CompanyDetailSchema,
  CompanyPageColumnPostsSchema,
  GroupCompanies,
  NativeAdPostSchema,
  Recruitment,
  type SameIndustryCompanies,
} from '../entities/companyDetail/schema';

type GetCompanyDetailsDataParams = {
  version?: string;
  companyId: string;
  year?: string;
  previewCode?: string;
  webSeminarLimit?: number;
};

type GetNativeAdPostsParams = {
  companyId: string;
  version?: string;
};

type GetRecruitmentsParams = {
  companyId: string;
  version?: string;
  limit?: number;
};

type GetGroupCompaniesParams = {
  companyId: string;
  version?: string;
};

/**
 * GetCompanyDetail
 */
export type GetCompanyDetailResponse = z.infer<typeof CompanyDetailSchema>;

export type ResultGetCompanyDetail = Result<GetCompanyDetailResponse, AppError>;

export type GetNativeAdPostsResponse = z.infer<typeof NativeAdPostSchema>;

export type ResultGetNativeAdPosts = Result<GetNativeAdPostsResponse, AppError>;

export type GetCompanyPageColumnPostsResponse = z.infer<
  typeof CompanyPageColumnPostsSchema
>;

export type ResultGetCompanyPageColumnPosts = Result<
  GetCompanyPageColumnPostsResponse,
  AppError
>;

export type GetRecruitmentsResponse = z.infer<typeof Recruitment>;

export type ResultGetRecruitments = Result<GetRecruitmentsResponse, AppError>;

export type GetSameIndustryCompaniesResponse = z.infer<
  typeof SameIndustryCompanies
>;

export type ResultGetSameIndustryCompanies = ResultWithOauthToken<
  GetSameIndustryCompaniesResponse,
  AppError
>;

export type GetGroupCompaniesResponse = z.infer<typeof GroupCompanies>;

export type ResultGetGroupCompanies = ResultWithOauthToken<
  GetGroupCompaniesResponse,
  AppError
>;

/**
 * 企業詳細データを取得する
 * GET /3.0/company/:id/detail?year={year}
 */
export const getCompanyDetail = async ({
  companyId,
  year,
  previewCode,
  webSeminarLimit,
  version = DEFAULT_API_VERSION,
}: GetCompanyDetailsDataParams): Promise<ResultGetCompanyDetail> => {
  let param = {};
  if (year && year.length > 0) {
    param = {...param, year};
  }

  if (previewCode) {
    param = {...param, previewCode};
  }

  if (webSeminarLimit) {
    param = {...param, webSeminarLimit};
  }

  const path = createPathWithQuery(
    `/${version}/companies/${companyId}/detail`,
    param,
  );
  const result: Result<GetCompanyDetailResponse, AppError> =
    await defaultClient.get(path, CompanyDetailSchema);
  if (!result.ok && result.error !== NotFoundError) {
    throw new Error(result.error);
  }

  return result;
};

/**
 * ネイティブアド用特集記事取得
 */
export const getNativeAdPosts = async ({
  companyId,
  version = DEFAULT_API_VERSION,
}: GetNativeAdPostsParams): Promise<ResultGetNativeAdPosts> => {
  const path = createPathWithQuery(`/${version}/column/posts`, {
    type: 'nativeAd',
    companyID: companyId,
  });
  const result: Result<GetNativeAdPostsResponse, AppError> =
    await defaultClient.get(path, NativeAdPostSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useGetNativeAdPosts = ({
  companyId,
  version,
  config,
}: GetNativeAdPostsParams & {
  config?: UseQueryOptions<ResultGetNativeAdPosts>;
}) => {
  return useQuery<ResultGetNativeAdPosts>({
    ...config,
    queryKey: ['nativeAdPosts', companyId],
    async queryFn() {
      return getNativeAdPosts({companyId, version});
    },
  });
};

/**
 * 企業配下ページ取得
 */
export const getCompanyPageColumnPosts = async ({
  companyId,
  version = DEFAULT_API_VERSION,
}: GetNativeAdPostsParams): Promise<ResultGetCompanyPageColumnPosts> => {
  const path = `/${version}/column/posts?type=companyPage&companyID=${companyId}`;
  const result: Result<GetCompanyPageColumnPostsResponse, AppError> =
    await defaultClient.get(path, CompanyPageColumnPostsSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useGetCompanyPageColumnPosts = ({
  companyId,
  version,
  config,
}: GetNativeAdPostsParams & {
  config?: UseQueryOptions<ResultGetCompanyPageColumnPosts>;
}) => {
  return useQuery<ResultGetCompanyPageColumnPosts>({
    ...config,
    queryKey: ['companyPageColumnPosts', companyId],
    async queryFn() {
      return getCompanyPageColumnPosts({companyId, version});
    },
  });
};

export const getRecruitments = async ({
  companyId,
  limit,
}: GetRecruitmentsParams): Promise<ResultGetRecruitments> => {
  const path = createPathWithQuery('/2.1/recruitment', {
    companyIDs: companyId,
    limit,
  });
  const result: Result<GetRecruitmentsResponse, AppError> =
    await defaultClient.get(path, Recruitment);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useGetRecruitments = ({
  companyId,
  version,
  limit,
  config,
}: GetRecruitmentsParams & {
  config?: UseQueryOptions<ResultGetRecruitments>;
}) => {
  return useQuery<ResultGetRecruitments>({
    ...config,
    queryKey: ['recruitments', companyId, limit],
    async queryFn() {
      return getRecruitments({companyId, version, limit});
    },
  });
};

export const getGroupCompanies = async ({
  companyId,
  version = DEFAULT_API_VERSION,
}: GetGroupCompaniesParams): Promise<ResultGetGroupCompanies> => {
  const path = `/${version}/companies/${companyId}/grouped-companies`;
  const result: Result<GetGroupCompaniesResponse, AppError> =
    await defaultClient.get(path, GroupCompanies);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useGetGroupCompanies = ({
  companyId,
  version,
  config,
}: GetGroupCompaniesParams & {
  config?: UseQueryOptions<ResultGetGroupCompanies>;
}) => {
  return useQuery<ResultGetGroupCompanies>({
    ...config,
    queryKey: ['groupCompanies', companyId],
    async queryFn() {
      return getGroupCompanies({companyId, version});
    },
  });
};

type UseGetCompanyDetailsOptions = {
  config?: UseQueryOptions<ResultGetCompanyDetail>;
} & GetCompanyDetailsDataParams;

export const useGetCompanyDetail = ({
  companyId,
  year,
  previewCode,
  version,
  config,
}: UseGetCompanyDetailsOptions) => {
  return useQuery<ResultGetCompanyDetail>({
    ...config,
    queryKey: ['companyDetail', companyId, year, previewCode],
    async queryFn() {
      return getCompanyDetail({companyId, year, previewCode, version});
    },
  });
};
